import Timeline from "@mui/lab/Timeline";
import { Box, CircularProgress, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { NEUTRAL } from "../../../theme/tokens";
import type { AgentActionHistory } from "../../../utils/models";
import { AgentActionType } from "../../../utils/models";
import { TimelineItem } from "./TimelineItem";

interface AgentHistoryProps {
	agentHistory: AgentActionHistory[];
	agentFuture: AgentActionHistory[];
	isLoadingAgentHistory: boolean;
	isPendingFuture: boolean;
	handleEscalate: (action: AgentActionHistory) => void;
	getCallNurseName?: (action: AgentActionHistory) => string | undefined;
	isCalling?: (action: AgentActionHistory) => boolean;
	skipAnimation?: boolean;
}

const LoadingAgentActivity = ({ isPendingFuture }: { isPendingFuture?: boolean }) => {
	return (
		<Box sx={{ px: 2, py: 2, color: NEUTRAL[500], fontStyle: "italic", display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
			<CircularProgress size="20px" />
			<Typography variant="body2">Loading {isPendingFuture ? "future" : "agent"} activity...</Typography>
		</Box>
	);
};

export const NotificationsAgent: FC<AgentHistoryProps> = ({
	agentHistory,
	agentFuture,
	isLoadingAgentHistory,
	isPendingFuture,
	handleEscalate,
	getCallNurseName,
	isCalling,
	skipAnimation = false,
}) => {
	const timelineItems = (agentHistory ?? []).sort((a, b) => DateTime.fromISO(a.created_at).valueOf() - DateTime.fromISO(b.created_at).valueOf());
	const futureTimelineItems = (agentFuture ?? []).sort((a, b) => DateTime.fromISO(a.created_at).valueOf() - DateTime.fromISO(b.created_at).valueOf());

	const futureItemsStartDelay = timelineItems.length * 200 + 300;

	const cumulativeNotified = useMemo(() => {
		return timelineItems.reduce((sum, current) => {
			if (!current.skip_reason && current.action_type !== AgentActionType.Evaluated) {
				return sum + (current.nurse_actions?.length || 0);
			}
			return sum;
		}, 0);
	}, [timelineItems]);

	// Helper function to get animation styles
	const getAnimationStyles = (index: number, isRecent: boolean, isFutureItem: boolean, shouldSkip: boolean) => {
		if (shouldSkip) {
			return {};
		}

		const duration = isRecent ? "0.1s" : "0.4s";

		let delay: string;
		if (isRecent) {
			delay = "0ms";
		} else if (isFutureItem) {
			// Calculate when the last history item finishes animating
			const lastHistoryDelay = (timelineItems.length - 1) * 400; // delay of last history item
			const lastHistoryDuration = 400; // duration in ms (0.4s)
			const totalHistoryTime = lastHistoryDelay + lastHistoryDuration;

			// Future items start after history completes, plus a small gap
			const startDelay = totalHistoryTime + 300;
			delay = `${startDelay + index * 200}ms`;
		} else {
			delay = `${index * 400}ms`;
		}

		return {
			opacity: 0,
			animation: `fadeInUp ${duration} ease-out forwards`,
			animationDelay: delay,
			"@keyframes fadeInUp": {
				"0%": {
					opacity: 0,
					transform: "translateY(10px)",
				},
				"100%": {
					opacity: 1,
					transform: "translateY(0)",
				},
			},
		};
	};

	return (
		<Timeline
			sx={{
				"& .MuiTimelineItem-root:before": {
					flex: 0,
				},
			}}
		>
			<TimelineItem action="posted" cumulativeNotified={0} />
			{isLoadingAgentHistory ? (
				<LoadingAgentActivity />
			) : (
				timelineItems.map((action, index) => {
					const cumulativeNotified = timelineItems.slice(0, index + 1).reduce((sum, current) => {
						if (!current.skip_reason && current.action_type !== AgentActionType.Evaluated) {
							return sum + (current.nurse_actions?.length || 0);
						}
						return sum;
					}, 0);

					const actionDate = DateTime.fromISO(action.created_at);
					const now = DateTime.now();
					const isRecentlyEscalated = now.diff(actionDate, "second").seconds < 5;

					return (
						<Box key={action.created_at} sx={getAnimationStyles(index, isRecentlyEscalated, false, skipAnimation)}>
							<TimelineItem
								action={action}
								hideSeperator={index === timelineItems.length - 1 && !isPendingFuture && futureTimelineItems.length === 0}
								dashedSeperator={index === timelineItems.length - 1}
								cumulativeNotified={cumulativeNotified}
								getCallNurseName={getCallNurseName}
							/>
						</Box>
					);
				})
			)}
			{isLoadingAgentHistory ? null : isPendingFuture ? (
				<LoadingAgentActivity isPendingFuture />
			) : (
				futureTimelineItems.map((action, index) => {
					return (
						<Box key={`${action.created_at}-${index}`} sx={getAnimationStyles(index, false, true, skipAnimation)}>
							<TimelineItem
								action={action}
								dashedSeperator
								isFuture
								hideSeperator={index === futureTimelineItems.length - 1}
								cumulativeNotified={cumulativeNotified + (action.nurse_actions?.length || 0)}
								onEscalate={() => handleEscalate(action)}
								getCallNurseName={getCallNurseName}
								isCalling={isCalling?.(action) ?? false}
							/>
						</Box>
					);
				})
			)}
		</Timeline>
	);
};
